import { useState, useEffect, useCallback } from "react";
import { FileSpreadsheet, FileText, FlaskConical } from "lucide-react";
import * as XLSX from "xlsx";
import { api, rd, printDoc, paymentLabel } from "./posShared";

export default function Reportes() {
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [samples, setSamples] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (frm) params.append("frm", frm);
    if (to) params.append("to", to);
    api.get(`/pos/reports?${params}`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/pos/samples-report?${params}`).then((r) => setSamples(r.data)).catch(() => {});
  }, [frm, to]);

  useEffect(() => { load(); }, [load]);

  const rangeLabel = `${frm || "inicio"} a ${to || "hoy"}`;

  const exportXlsx = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    const resumen = [
      ["Reporte de ventas — MO by Vir"],
      ["Rango", rangeLabel],
      [],
      ["Ventas totales", data.total_sales],
      ["Facturas", data.invoice_count],
      ["Ganancia estimada", data.estimated_profit],
      ["Valor inventario", data.inventory_value],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resumen), "Resumen");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      data.top_products.map((p) => ({ Producto: p.name, Cantidad: p.qty }))), "Más vendidos");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      data.by_payment_method.map((m) => ({ Método: paymentLabel(m.method), Total: m.total }))), "Por método");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      data.pending.map((p) => ({ Factura: p.number, Cliente: p.customer_name, Balance: p.balance }))), "Pendientes");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      data.voided.map((p) => ({ Factura: p.number, Cliente: p.customer_name, Motivo: p.void_reason || "" }))), "Anuladas");
    XLSX.writeFile(wb, `reporte-mobyvir-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportPdf = () => {
    if (!data) return;
    const tp = data.top_products.map((p) => `<tr><td>${p.name}</td><td class="r">${p.qty} uds</td></tr>`).join("");
    const pm = data.by_payment_method.map((m) => `<tr><td>${paymentLabel(m.method)}</td><td class="r">${rd(m.total)}</td></tr>`).join("");
    const pend = data.pending.map((p) => `<tr><td>${p.number} · ${p.customer_name}</td><td class="r">${rd(p.balance)}</td></tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Reporte MO by Vir</title>
      <style>
        @page { size: A4; margin: 16mm; }
        body { font-family: 'Helvetica Neue', Arial, sans-serif; color:#2C2621; font-size: 13px; }
        h1 { color:#B76E79; font-size: 26px; margin:0; }
        .sub { color:#888; font-size: 12px; margin: 2px 0 20px; }
        .cards { display:flex; gap:12px; margin-bottom: 24px; }
        .card { flex:1; border:1px solid #eee; border-radius:8px; padding:12px; }
        .card .l { font-size: 10px; text-transform:uppercase; color:#999; letter-spacing:1px; }
        .card .v { font-size: 18px; font-weight:600; margin-top:4px; }
        h2 { font-size: 14px; color:#B76E79; border-bottom:2px solid #F5F2EB; padding-bottom:4px; margin-top:24px; }
        table { width:100%; border-collapse:collapse; font-size:12px; }
        td { padding:6px 4px; border-bottom:1px solid #f0f0f0; }
        .r { text-align:right; }
      </style></head><body>
      <h1>Reporte de ventas — MO by Vir</h1>
      <div class="sub">Rango: ${rangeLabel} · Generado ${new Date().toLocaleString("es-DO")}</div>
      <div class="cards">
        <div class="card"><div class="l">Ventas totales</div><div class="v">${rd(data.total_sales)}</div></div>
        <div class="card"><div class="l">Facturas</div><div class="v">${data.invoice_count}</div></div>
        <div class="card"><div class="l">Ganancia estimada</div><div class="v">${rd(data.estimated_profit)}</div></div>
        <div class="card"><div class="l">Valor inventario</div><div class="v">${rd(data.inventory_value)}</div></div>
      </div>
      <h2>Productos más vendidos</h2><table>${tp || '<tr><td>Sin datos</td></tr>'}</table>
      <h2>Ventas por método de pago</h2><table>${pm || '<tr><td>Sin datos</td></tr>'}</table>
      <h2>Cuentas pendientes de cobro</h2><table>${pend || '<tr><td>Sin datos</td></tr>'}</table>
      </body></html>`;
    printDoc(html);
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <h1 className="font-serif text-3xl">Reportes</h1>
        <div className="flex items-end gap-2 flex-wrap">
          <div><label className="text-[11px] uppercase text-neutral-500">Desde</label><input type="date" value={frm} onChange={(e) => setFrm(e.target.value)} className="block bg-white border border-neutral-200 rounded-lg px-3 py-2 text-sm" /></div>
          <div><label className="text-[11px] uppercase text-neutral-500">Hasta</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="block bg-white border border-neutral-200 rounded-lg px-3 py-2 text-sm" /></div>
          <button data-testid="pos-report-excel" onClick={exportXlsx} className="inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-rose transition"><FileSpreadsheet size={14} /> Excel</button>
          <button data-testid="pos-report-pdf" onClick={exportPdf} className="inline-flex items-center gap-2 border border-espresso text-espresso rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-espresso hover:text-cream transition"><FileText size={14} /> PDF</button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Stat l="Ventas totales" v={rd(data.total_sales)} />
            <Stat l="Facturas" v={data.invoice_count} />
            <Stat l="Ganancia estimada" v={rd(data.estimated_profit)} accent />
            <Stat l="Valor inventario" v={rd(data.inventory_value)} />
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <Panel title="Productos más vendidos">
              {data.top_products.length ? data.top_products.map((p, i) => (
                <Line key={i} l={p.name} v={`${p.qty} uds`} />
              )) : <Empty />}
            </Panel>
            <Panel title="Ventas por método de pago">
              {data.by_payment_method.length ? data.by_payment_method.map((m, i) => (
                <Line key={i} l={<span>{paymentLabel(m.method)}</span>} v={rd(m.total)} />
              )) : <Empty />}
            </Panel>
            <Panel title="Cuentas pendientes de cobro">
              {data.pending.length ? data.pending.map((p) => (
                <Line key={p.id} l={`${p.number} · ${p.customer_name}`} v={rd(p.balance)} />
              )) : <Empty />}
            </Panel>
            <Panel title="Facturas anuladas">
              {data.voided.length ? data.voided.map((p) => (
                <Line key={p.id} l={`${p.number} · ${p.customer_name}`} v={p.void_reason || ""} />
              )) : <Empty />}
            </Panel>
          </div>

          {samples && (
            <div className="bg-white border border-neutral-200 rounded-xl p-5 mt-6" data-testid="pos-samples-report">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h2 className="font-medium flex items-center gap-2"><FlaskConical size={18} className="text-rose" /> Muestras y probadores</h2>
                <div className="flex gap-4 text-sm">
                  <span className="text-neutral-500">Unidades: <strong className="text-espresso">{samples.total_units}</strong></span>
                  <span className="text-neutral-500">Costo estimado: <strong className="text-espresso">{rd(samples.total_cost_value)}</strong></span>
                </div>
              </div>
              {samples.by_product.length ? (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[11px] uppercase text-neutral-500 border-b border-neutral-100"><th className="py-2">Producto</th><th className="py-2 text-center">Unidades usadas</th><th className="py-2 text-right">Costo</th></tr></thead>
                  <tbody>
                    {samples.by_product.map((p, i) => (
                      <tr key={i} className="border-b border-neutral-50"><td className="py-2">{p.product_name}</td><td className="py-2 text-center">{p.units}</td><td className="py-2 text-right font-medium">{rd(p.cost_value)}</td></tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-neutral-400 text-sm py-4 text-center">No se han registrado muestras/probadores en este período. Regístralas en Inventario con el motivo «Muestra / probador».</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const Stat = ({ l, v, accent }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-4">
    <p className="text-[11px] uppercase tracking-wider text-neutral-500">{l}</p>
    <p className={`text-2xl font-serif mt-1 ${accent ? "text-emerald-600" : "text-espresso"}`}>{v}</p>
  </div>
);
const Panel = ({ title, children }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-5">
    <h2 className="font-medium mb-3">{title}</h2>
    <div className="space-y-2 max-h-64 overflow-y-auto">{children}</div>
  </div>
);
const Line = ({ l, v }) => <div className="flex justify-between text-sm border-b border-neutral-50 py-1.5"><span className="text-neutral-600">{l}</span><span className="font-medium">{v}</span></div>;
const Empty = () => <p className="text-neutral-400 text-sm py-4 text-center">Sin datos.</p>;
